import Button from '@mui/material/Button';
import {AppBar, Card, CardActions, CardContent, CardHeader, ListItemButton, CircularProgress } from '@mui/material';
import React, {ReactElement, useEffect} from 'react';
import Stack from '@mui/material/Stack';
import {useWorkspaceContext} from '../../../../../context/workspaceContext';
import {uuid} from '../../../../../utils/utils';
import IRPService from '../../../../../services/irp.service';
import {toast} from 'react-toastify';
import EventSequence from '../../../../../@types/event-sequence';
import {CanvasActionType} from '../../../../../@types/canvas-action-type';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/HighlightOff';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import * as CanvasUtils from '../../../../../utils/canvas/node-utils';
import {red} from '@mui/material/colors';
import { ReportMappingGroupDTO, ReportMappingDTO } from '../../../../../@types/integrated-risk-picture';
import { ReportsMappedToNode, SimpleLinkedOccurrenceDna, SimpleLinkedOccurrenceDnaElement } from '../../../../../@types/mapping-dna';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';

// Standalone component for Successes list
function SuccessList({ successes, selectedIndex, onSelect }: {
    successes: ReportMappingGroupDTO[],
    selectedIndex: number,
    onSelect: (index: number, occurrenceLink: ReportMappingGroupDTO) => void
}): React.ReactElement {
    return (
        <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1, minWidth: '150px', border: '1px solid lightgrey', borderRadius: '4px', padding: '8px' }}>
            <Chip
                icon={<GppGoodIcon color='success' />}
                label={`Successes: ${successes.length}`}
                variant="outlined"
            />
            <List
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    position: 'relative',
                    overflow: 'auto',
                    maxHeight: 200,
                }}
                dense
                disablePadding
            >
                {successes.map((occurrenceLink: ReportMappingGroupDTO, index: number) => (
                    <ListItem key={occurrenceLink.reportProcessingId + '-' + index}>
                        <ListItemButton
                            selected={selectedIndex === index}
                            onClick={(): void => onSelect(index, occurrenceLink)}
                        >
                            <ListItemText
                                primary={occurrenceLink.reportIdentifier}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Stack>
    );
}

// Standalone component for Failures list
function FailureList({ failures, selectedIndex, onSelect }: {
    failures: ReportMappingGroupDTO[],
    selectedIndex: number,
    onSelect: (index: number, occurrenceLink: ReportMappingGroupDTO) => void
}): React.ReactElement {
    return (
        <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1, minWidth: '150px', border: '1px solid lightgrey', borderRadius: '4px', padding: '8px' }}>
            <Chip
                icon={<GppBadIcon color='error' />}
                label={`Failures: ${failures.length}`}
                variant="outlined"
            />
            <List
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    position: 'relative',
                    overflow: 'auto',
                    maxHeight: 200,
                }}
                dense
                disablePadding
            >
                {failures.map((occurrenceLink: ReportMappingGroupDTO, index: number) => (
                    <ListItem key={occurrenceLink.reportProcessingId + '-' + index}>
                        <ListItemButton
                            selected={selectedIndex === index}
                            onClick={(): void => onSelect(index, occurrenceLink)}
                        >
                            <ListItemText
                                primary={occurrenceLink.reportIdentifier}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Stack>
    );
}

export default function PanelODnaList(props: { eventSequence: EventSequence, isActive: boolean, onDnaToggle: (openDnaPanel: boolean | string[]) => void }): ReactElement {
    const {   eventSequence, isActive, onDnaToggle } = props;
    
    const { currentCanvasAction, setCurrentCanvasAction, currentCanvasSelectedNode } = useWorkspaceContext();
    const [open, setOpen] = React.useState(false);
    const [selectedNodeOccurrenceList, setSelectedNodeOccurrenceList] = React.useState<ReportsMappedToNode[]>([]);
    const [selectedIndexList1, setSelectedIndexList1] = React.useState(-1);
    const [selectedIndexList2, setSelectedIndexList2] = React.useState(-1);
    const [loading, setLoading] = React.useState<boolean>(false);
    const uuidRef = React.useRef(uuid());

    const isEvent = (): boolean => {
        if (!currentCanvasSelectedNode) {
            return false;
        }
        return CanvasUtils.isEvent(currentCanvasSelectedNode.type);
    }

    const getDNA = (irpId: number, reportProcessingId: number): void => {
        IRPService.getReportMappings(irpId, reportProcessingId).then((response) => {
            drawMapping(irpId, response);
        }).catch((error: Error) => {
            toast.error(error.message);
        });
    }

    useEffect(() => {
        if (isActive && currentCanvasAction !== null && currentCanvasAction.actionType !== CanvasActionType.UNKNOWN) {
            switch (currentCanvasAction.actionType) {
                case CanvasActionType.CANVAS_TOGGLE_DNA_LIST:
                    setTimeout(() => onDnaToggle(!open));
                    setOpen(!open);
                    if (open) {                  
                        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PULLED_ODNA, query: null });
                        setSelectedNodeOccurrenceList([]);
                    }
                    break;
                case CanvasActionType.NODE_OCCURRENCE_LIST_VIEW:
                    if (currentCanvasSelectedNode) {
                        const node = currentCanvasSelectedNode;
                        const { startDate, endDate, irpId, irpName } = currentCanvasAction.query;
                        const elementType = isEvent() ? 'EVENT' : 'FAULT_TREE_NODE';
                        setLoading(true);
                        IRPService.getIrpMappings(irpId, 0, 100, undefined, node.id, elementType, startDate, endDate).then((response) => {
                            let reportsMapped: ReportsMappedToNode = {
                                nodeType: elementType,
                                nodeId: node.id,
                                nodeUniqueId: node.uniqueId || '',
                                nodeName: node.name || '',
                                irpId: irpId,
                                irpName: irpName || '',
                                occurrenceLinks: response
                            };
                            setSelectedNodeOccurrenceList(selectedNodeOccurrenceList.concat(reportsMapped));
                            if (!open) {
                                setTimeout(() => onDnaToggle(true));
                                setOpen(true);
                            }
                        }).catch((error: Error) => {
                            toast.error(error.message);
                        }).finally(() => {
                            setLoading(false);
                        });
                    }
                    break;
                default:
                    break;
            }
            setTimeout(() => {
                setCurrentCanvasAction({ actionType: CanvasActionType.UNKNOWN });
            }, 100);

        }

    }, [currentCanvasAction, isActive]);

    const drawMapping = (irpId: number, mapping: ReportMappingDTO | null): void => {
        if (mapping) {
            let events: SimpleLinkedOccurrenceDnaElement[] = mapping.nodeMappings?.filter((item) => item.nodeType !== 'FAULT_TREE_NODE').map((item) => ({ id: item.nodeId, justification: item.justification ?? '', failureStatus: 'FAILURE', relevant: item.relevant ?? true })) || [];  
            let faultTreeNodes: SimpleLinkedOccurrenceDnaElement[] = mapping.nodeMappings?.filter((item) => item.nodeType === 'FAULT_TREE_NODE').map((item) => ({ id: item.nodeId, justification: item.justification ?? '', failureStatus: 'FAILURE', relevant: item.relevant ?? true })) || [];
            let dna: SimpleLinkedOccurrenceDna = {
                irpId: irpId,
                occurrenceId: mapping.reportId,
                reportProcessingId: mapping.reportProcessingId!,
                occurrenceStatus: mapping.processingStatus,
                events: events,
                faultTreeNodes: faultTreeNodes,
                eventSequenceId: eventSequence.id,
                eventIds: events.map((item) => item.id),
                faultTreeNodeIds: faultTreeNodes.map((item) => item.id)
            };
            setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_PULLED_ODNA, query: dna });
        }
    };

    const handlePrevPage = (itemIndex: number): void => {
        const item = selectedNodeOccurrenceList[itemIndex];
        const prevPage = Math.max(0, item.occurrenceLinks.pageNumber - 1);
        IRPService.getIrpMappings(item.irpId, prevPage, item.occurrenceLinks.pageSize)
            .then((response) => {
                let updated = selectedNodeOccurrenceList.slice();
                updated[itemIndex] = { ...item, occurrenceLinks: response };
                setSelectedNodeOccurrenceList(updated);
            })
            .catch((error: Error) => {
                toast.error(error.message);
            });
    };

    const handleNextPage = (itemIndex: number): void => {
        const item = selectedNodeOccurrenceList[itemIndex];
        const nextPage = item.occurrenceLinks.pageNumber + 1;
        const maxPage = Math.ceil(item.occurrenceLinks.totalElements / item.occurrenceLinks.pageSize) - 1;
        if (nextPage > maxPage) return;
        IRPService.getIrpMappings(item.irpId, nextPage, item.occurrenceLinks.pageSize)
            .then((response) => {
                let updated = selectedNodeOccurrenceList.slice();
                updated[itemIndex] = { ...item, occurrenceLinks: response };
                setSelectedNodeOccurrenceList(updated);
            })
            .catch((error: Error) => {
                toast.error(error.message);
            });
    }

    if (!open) {
        return <></>;
    }


    return loading ? (
        <CircularProgress />
    ) : (
        <Paper id={uuidRef.current} sx={{ height: '100%', overflow: 'auto' }}>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static" color="primary">
                    <Toolbar>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            Node Occurrence List Panel
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />

                        <IconButton
                            color="inherit"
                            component='button'
                            edge='end'
                            onClick={(): void => setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_DNA_LIST })}>
                            <CloseIcon sx={{ width: 25, height: 25 }} />
                        </IconButton>
                    </Toolbar>
                </AppBar>
            </Box>
            <Stack direction="column" spacing={2} justifyContent='start' sx={{ m: 1 }}>
                {selectedNodeOccurrenceList.map((item, index) => {
                    let failures = item.occurrenceLinks.content;
                    let successes: ReportMappingGroupDTO[] = [];
                    return (
                        <Card key={item.nodeId + '-' + index} sx={{ width: '100%' }} variant='outlined'>
                            <CardHeader
                                avatar={<Avatar sx={{ bgcolor: red[100] }}>N</Avatar>}
                                title={`${item.irpName} - ${item.nodeUniqueId}`}
                                subheader={item.nodeName}
                            />
                            <CardContent>
                                <Stack direction="row" spacing={2} justifyContent='start' sx={{ m: 1 }}>        
                                    <SuccessList
                                        successes={successes}
                                        selectedIndex={selectedIndexList1}
                                        onSelect={(index, occurrenceLink): void => {
                                            getDNA(item.irpId, occurrenceLink.reportProcessingId);
                                            setSelectedIndexList1(index);
                                            setSelectedIndexList2(-1);
                                        }}
                                    />
                                    <FailureList
                                        failures={failures}
                                        selectedIndex={selectedIndexList2}
                                        onSelect={(index, occurrenceLink): void => {
                                            getDNA(item.irpId, occurrenceLink.reportProcessingId);
                                            setSelectedIndexList2(index);
                                            setSelectedIndexList1(-1);
                                        }}
                                    />
                                </Stack>
                                {/* Pagination controls for mappings */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, mb: 1}}>
                                    <Button
                                        onClick={(): void => handlePrevPage(index)}
                                        disabled={item.occurrenceLinks.pageNumber <= 0}
                                    >Prev</Button>
                                    <Typography variant="body2">
                                        Page {item.occurrenceLinks.pageNumber + 1} of {Math.ceil(item.occurrenceLinks.totalElements / item.occurrenceLinks.pageSize)}
                                    </Typography>
                                    <Button
                                        onClick={(): void => handleNextPage(index)}
                                        disabled={item.occurrenceLinks.pageNumber >= Math.ceil(item.occurrenceLinks.totalElements / item.occurrenceLinks.pageSize) - 1}
                                    >Next</Button>
                                </Box>
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={(): void => {
                                        let textInput = '';
                                        item.occurrenceLinks.content.forEach((occurrenceLink: ReportMappingGroupDTO) => {
                                            textInput += occurrenceLink.reportIdentifier + ',' + occurrenceLink.reportSource + ',' + occurrenceLink.reportSummary + ',' + occurrenceLink.totalMappings + '\n';
                                        });
                                        const element = document.createElement('a');
                                        const file = new Blob([textInput], { type: 'text/plain' });
                                        element.href = URL.createObjectURL(file);
                                        element.download = 'occurrences_' + item.nodeUniqueId + '_download.csv';
                                        document.body.appendChild(element); // Required for this to work in FireFox
                                        element.click();
                                        document.body.removeChild(element);
                                    }}
                                >
                                    Download
                                </Button>
                            </CardActions>
                        </Card>
                    );
                })}
            </Stack>

        </Paper>
    );
}
